import { useCallback } from 'react';
import type { RefObject } from 'react';

export interface UseAudioTransportReturn {
  play: () => void;
  /**
   * Reproduce un tramo del audio (oración del quiz, 19.G): seek a `start` y
   * pausa automática al llegar a `end` — el listener `timeupdate` del provider
   * corta al cruzar `segmentEndRef`. Cualquier play/seek manual cancela el corte.
   */
  playSegment: (start: number, end: number) => void;
  pause: () => void;
  toggle: () => void;
  /** Cierra la barra flotante global: pausa + la oculta (no destruye la sesión). */
  close: () => void;
  seek: (sec: number) => void;
  skip: (delta: number) => void;
  setSpeed: (s: number) => void;
}

/**
 * Transport del único `<audio>` del feature Escuchar. Opera sobre los refs que
 * el provider comparte con sus listeners (`segmentEndRef` = fin del tramo de
 * `playSegment`; el corte real vive en el `timeupdate` del provider).
 */
export function useAudioTransport(args: {
  audioRef: RefObject<HTMLAudioElement | null>;
  segmentEndRef: RefObject<number | null>;
  setPos: (sec: number) => void;
  setDismissed: (dismissed: boolean) => void;
  /** Setter del estado de velocidad (pref persistida por el provider). */
  setSpeedState: (s: number) => void;
}): UseAudioTransportReturn {
  const { audioRef, segmentEndRef, setPos, setDismissed, setSpeedState } = args;

  const play = useCallback((): void => {
    segmentEndRef.current = null; // play manual = reproducción normal, sin corte
    void audioRef.current?.play().catch(() => undefined);
  }, [audioRef, segmentEndRef]);

  const playSegment = useCallback(
    (start: number, end: number): void => {
      const audio = audioRef.current;
      if (!audio || end <= start) return;
      const d = Number.isFinite(audio.duration) ? audio.duration : 0;
      audio.currentTime = Math.min(Math.max(0, start), d || start);
      setPos(audio.currentTime);
      segmentEndRef.current = end;
      void audio.play().catch(() => undefined);
    },
    [audioRef, segmentEndRef, setPos],
  );

  const pause = useCallback((): void => {
    audioRef.current?.pause();
  }, [audioRef]);

  const toggle = useCallback((): void => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) void audio.play().catch(() => undefined);
    else audio.pause();
  }, [audioRef]);

  // La sesión sigue (player / home resume); el 'play' event la vuelve a
  // mostrar (onPlay → setDismissed(false) en el provider).
  const close = useCallback((): void => {
    audioRef.current?.pause();
    setDismissed(true);
  }, [audioRef, setDismissed]);

  const seek = useCallback(
    (sec: number): void => {
      const audio = audioRef.current;
      if (!audio) return;
      segmentEndRef.current = null; // un seek manual cancela el tramo activo
      const d = Number.isFinite(audio.duration) ? audio.duration : 0;
      audio.currentTime = Math.min(Math.max(0, sec), d || sec);
      setPos(audio.currentTime);
    },
    [audioRef, segmentEndRef, setPos],
  );

  const skip = useCallback(
    (delta: number): void => {
      seek((audioRef.current?.currentTime ?? 0) + delta);
    },
    [seek, audioRef],
  );

  const setSpeed = useCallback(
    (s: number): void => {
      setSpeedState(s);
      if (audioRef.current) audioRef.current.playbackRate = s;
    },
    [audioRef, setSpeedState],
  );

  return { play, playSegment, pause, toggle, close, seek, skip, setSpeed };
}
